import React, { useEffect, useState } from 'react';
import api from '../services/api';
import './CompanyName.css';

/**
 * The company (or coin) name under a ticker, on every screener and portfolio
 * card (2026-10-05). He asked for it as a safeguard: a ticker that grades
 * fine but is the WRONG company is worse than an error, and it has happened.
 *
 * Every <CompanyName> on a page joins one request: lookups queue for a tick
 * and go out together, and names are cached for the session. `crypto` matters:
 * "BTC" names the coin (Bitcoin) only when told it is crypto; as an equity
 * symbol it is a Grayscale ETF, and the name should say what was graded.
 * Long names truncate with an ellipsis; the full name is the title (hover,
 * long-press).
 */
const cache = new Map();      // "c:BTC" | "e:AAPL" -> name | null
const waiters = new Map();    // key -> [setState, ...]
let queued = new Set();
let timer = null;

function flush() {
  timer = null;
  const batch = [...queued];
  queued = new Set();
  const eq = batch.filter((k) => k.startsWith('e:')).map((k) => k.slice(2));
  const cr = batch.filter((k) => k.startsWith('c:')).map((k) => k.slice(2));
  const done = (names) => {
    batch.forEach((k) => {
      const name = (names || {})[k.slice(2)] ?? null;
      cache.set(k, name);
      (waiters.get(k) || []).forEach((fn) => fn(name));
      waiters.delete(k);
    });
  };
  api.get('/public/names', { params: { symbols: eq.join(','), crypto: cr.join(',') } })
    .then(({ data }) => done(data?.names))
    .catch(() => done({}));
}

function request(key, setName) {
  if (cache.has(key)) { setName(cache.get(key)); return; }
  waiters.set(key, [...(waiters.get(key) || []), setName]);
  if (!queued.has(key)) queued.add(key);
  if (!timer) timer = setTimeout(flush, 30);
}

export default function CompanyName({ symbol, crypto = false, className = '' }) {
  const key = `${crypto ? 'c' : 'e'}:${(symbol || '').toUpperCase()}`;
  const [name, setName] = useState(() => (cache.has(key) ? cache.get(key) : undefined));

  useEffect(() => {
    if (!symbol) return undefined;
    let live = true;
    request(key, (n) => { if (live) setName(n); });
    return () => { live = false; };
  }, [key, symbol]);

  if (!name) return null;   // unknown or still loading: no empty line, no guess
  return <span className={`co-name ${className}`} title={name}>{name}</span>;
}
